import { BasketFragment } from '@graphql/fragments';
import { basketRequest } from '@hooks/basket/_utils';
import createLineItem from '@models/lineItem';
import { BasketLineItemData } from '@repo/ui/src/types/lineItem';
import { gql } from 'graphql-request';
import Cookies from 'js-cookie';
import { SWRConfiguration } from 'swr';
import useCurrentUrl from '@repo/ui/src/hooks/useCurrentUrl';
import { Basket, BasketData } from '@repo/ui/src/types/basket';
import Product from '@models/product';
import { getBasketToken, getShopTokens, setShopTokens } from '@utils/userTokens';
import { useCustomerIdToken } from '@hooks/customer';
import useSWRImmutable from 'swr/immutable';
import { GraphQLRequestWithTokenProps } from '@repo/ui/src/utils/graphql-request';

interface Key extends GraphQLRequestWithTokenProps {
  active?: boolean;
  locale: string;
}

const fetcher = async ({ active, ...key }: Key) => {
  if (!active) return;

  const isBasketLink = Cookies.get('isBasketLink') === 'true';
  if (isBasketLink) {
    localStorage.removeItem('sessionIdToken');
    setShopTokens({ basketJWT: Cookies.get('basketJWT'), shopToken: '' });
  }
  const { getBasket } = await basketRequest({ ...key, variables: { isBasketLink } });
  Cookies.set('isBasketLink', '');
  return formatBasket(getBasket, key.locale!);
};

export const formatBasket = (data: BasketData, locale: string): Basket => {
  const showRecommendations = locale === 'en-US';
  return {
    ...data,
    lineItems: data?.lineItems?.map((item: BasketLineItemData, i: number) =>
      createLineItem(item, i),
    ),
    recommendations: showRecommendations
      ? data?.recommendations?.map((rec, i: number) => new Product({ monetate: rec }, i))
      : [],
  };
};

export const getBasket = gql`
  ${BasketFragment}
  query GetBasket($isBasketLink: Boolean) {
    getBasket(isBasketLink: $isBasketLink) {
      ...Basket
    }
  }
`;

const useBasket = (config: SWRConfiguration = {}) => {
  const { locale, eCommEnabled } = useCurrentUrl();
  const idToken = useCustomerIdToken();

  const active = eCommEnabled && (getShopTokens().basketJWT || !!getBasketToken() || idToken);

  return useSWRImmutable<Basket | undefined>(
    {
      active,
      document: getBasket,
      locale,
      idToken,
    },
    fetcher,
    config,
  );
};

export const useBasketMutation = () => useBasket().mutate;

export default useBasket;
